import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/hooks/useApi';

/**
 * Backend deliberately returns only `isMember` (and `unavailable` on failure)
 * to portal callers. The underlying check knows whether the creator is in
 * the clipping Discord server vs has approved clips, but exposing those
 * details would let any authenticated outreacher probe arbitrary handles
 * for membership / clip activity. Admin-side surfaces can call the service
 * directly if they need the full breakdown.
 */
export interface MembershipCheckResult {
  isMember: boolean;
  unavailable?: boolean;
}

interface UseClippingMembershipOptions {
  channel: string;
  username: string;
  enabled?: boolean;
}

export function useClippingMembership({
  channel,
  username,
  enabled = true,
}: UseClippingMembershipOptions) {
  const api = useApi();
  return useQuery({
    queryKey: ['clipping-membership', channel, username],
    queryFn: () =>
      api.get<MembershipCheckResult>('/portal/invites/check-membership', {
        channel,
        username,
      }),
    enabled: enabled && !!channel && !!username,
    staleTime: 30_000,
  });
}
